import './template-entry-list-item.scss';

import { isEmpty, kebabCase } from 'lodash';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { getTemplateMinuteLength } from '../../data/data/utils';
import {
  TemplateEntryAppointmentItemDataFragment,
  TemplateEntryItemDataFragment,
} from '../../data/types/api/graphql';
import { formatTimeSpanToTime } from '../../data/utils/date.utils';
import { makeKeyDownHandler } from '../../data/utils/make-key-down-handler';
import { Button, ButtonIcon, ButtonType } from '../button/button';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';
import { TemplateEntryAppointmentList } from './template-entry-appointment-list';

export enum TemplateEntryListItemType {
  DEFAULT = 'DEFAULT',
}

type TemplateEntryListItemProps = {
  type?: TemplateEntryListItemType;
  templateEntryItem: TemplateEntryItemDataFragment;
  actions?: {
    icon?: ButtonIcon;
    onClick: (value: string) => void;
  }[];
  onClick?: (
    value:
      | TemplateEntryItemDataFragment
      | TemplateEntryAppointmentItemDataFragment
  ) => void;
};

export function TemplateEntryListItem({
  templateEntryItem,
  actions = [],
  onClick,
  ...props
}: TemplateEntryListItemProps) {
  const type = useMemo(() => {
    if (props.type) {
      return props.type;
    }

    return kebabCase(templateEntryItem.type);
  }, [props.type, templateEntryItem.type]);

  const attributes = useMemo(() => {
    if (onClick) {
      return {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick(templateEntryItem),
        onKeyDown: makeKeyDownHandler(() => onClick(templateEntryItem)),
      };
    }

    return {};
  }, [templateEntryItem, onClick]);

  return (
    <>
      <div
        {...attributes}
        className={`template-entry-list-item template-entry-list-item--${kebabCase(
          type
        )}`}
      >
        <div className="template-entry-list-item__time">
          <p>
            {formatTimeSpanToTime(templateEntryItem.minuteStartTime)} -{' '}
            {formatTimeSpanToTime(templateEntryItem.minuteEndTime)}
          </p>
        </div>
        <div className="template-entry-list-item__content">
          <p>
            <CustomFormattedMessage
              id={`template.entry-type-${kebabCase(
                templateEntryItem.type
              )}.title`}
              values={{
                name: templateEntryItem.name,
                minutes: getTemplateMinuteLength(templateEntryItem),
              }}
            />
          </p>
        </div>

        {!isEmpty(actions) && (
          <div className="template-entry-list-item__action">
            {actions.map(option => (
              <Button
                key={uuid()}
                type={ButtonType.TRANSPARENT}
                icon={option.icon}
                onClick={() => option.onClick(templateEntryItem.id)}
              />
            ))}
          </div>
        )}
      </div>

      <TemplateEntryAppointmentList
        items={templateEntryItem.appointments}
        actions={actions}
        onClick={onClick}
      />
    </>
  );
}
